import { yupResolver } from '@hookform/resolvers/yup'
import { Add } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'
import { useGetCreditReport } from '@src/data/api/credit-application/credit-application-api'
import { nanoid } from 'nanoid'
import { memo, useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog } from '../../../components'
import {
  AdditionnalBankruptcies,
  Applicant,
  ApplicantBankruptcies,
  Bankruptcy,
  CreditApplication,
  applicantBankruptciesSchema,
} from '../../../data/types'
import { reportErrorToConsole } from '../../../services/error-logger'
import BankruptcyComponent from './Bankruptcy'
import ReportedBankruptcy from './ReportedBankruptcy'

interface Props {
  open: boolean
  creditApplication: CreditApplication
  applicant: Applicant | undefined
  onConfirm: (data: ApplicantBankruptcies) => void
  onCancel: () => void
  disableConfirm?: boolean
}

const EditBankruptciesDialog = ({ applicant, creditApplication, onConfirm, onCancel, open, disableConfirm }: Props) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: applicant,
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver: yupResolver(applicantBankruptciesSchema),
  })

  const {
    fields: currentAdditionnalBankruptciesFields,
    append: additionnalBankruptciesAppend,
    remove: additionnalBankruptciesRemove,
  } = useFieldArray({
    name: 'additionnalBankruptcies',
    control,
  })

  const [creditReportData] = useGetCreditReport(
    creditApplication?.financingProgramId,
    creditApplication?.id,
    applicant?.hardHitReport?.creditReportId,
  )

  useEffect(() => {
    reset(applicant)
  }, [applicant, reset])

  return (
    <ActionsDialog
      disabled={disableConfirm}
      title={
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
          <Typography>{t('creditReport.bankruptcyHistory')}</Typography>
          <Button
            variant="contained"
            color="success"
            endIcon={<Add />}
            onClick={() => additionnalBankruptciesAppend({ id: nanoid() } as AdditionnalBankruptcies)}
          >
            {t('common.add')}
          </Button>
        </Stack>
      }
      open={open}
      onCancel={() => {
        reset(applicant)
        onCancel()
      }}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
    >
      {(creditReportData?.creditReport as { bankruptcies?: unknown[] })?.bankruptcies?.map((bankruptcy) => (
        <ReportedBankruptcy bankruptcy={bankruptcy as Bankruptcy} key={(bankruptcy as Bankruptcy).caseNumber} />
      ))}
      {currentAdditionnalBankruptciesFields.map((bankruptcy, index) => (
        <BankruptcyComponent
          index={index}
          register={register}
          error={errors?.additionnalBankruptcies}
          control={control}
          key={bankruptcy.id}
          isSyndicReported
          onDeleteClicked={() => additionnalBankruptciesRemove(index)}
          fieldName="additionnalBankruptcies"
        />
      ))}
    </ActionsDialog>
  )
}

export default memo(EditBankruptciesDialog)
